import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import {
  X,
  Calendar,
  Tag,
  ListTodo,
  Plus,
  Trash2,
  Check,
  FileText,
  ChevronLeft,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Calendar as CalendarComponent } from './ui/calendar';
import { format, isToday, isTomorrow, isPast } from 'date-fns';

// Separate inner component that handles the form state
const TaskDetailForm = ({ task, onClose, updateTask, deleteTask, lists, tags, addSubtask, deleteSubtask, toggleSubtaskComplete, setSelectedTaskId }) => {
  const [title, setTitle] = useState(task.title || '');
  const [notes, setNotes] = useState(task.notes || '');
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);

  const handleClose = () => {
    // Save changes before closing
    if (title !== task.title || notes !== task.notes) {
      updateTask(task.id, { title, notes });
    }
    setSelectedTaskId(null);
    if (onClose) onClose();
  };

  const handleTitleBlur = () => {
    if (title !== task.title) {
      updateTask(task.id, { title });
    }
  };

  const handleNotesBlur = () => {
    if (notes !== task.notes) {
      updateTask(task.id, { notes });
    }
  };

  const handleDateSelect = (date) => {
    updateTask(task.id, { dueDate: date?.toISOString() || null });
    setShowDatePicker(false);
  };

  const handleListChange = (listId) => {
    updateTask(task.id, { listId });
  };

  const handleToggleTag = (tagId) => {
    const currentTags = task.tagIds || [];
    const newTags = currentTags.includes(tagId)
      ? currentTags.filter(id => id !== tagId)
      : [...currentTags, tagId];
    updateTask(task.id, { tagIds: newTags });
  };

  const handleAddSubtask = () => {
    if (newSubtaskTitle.trim()) {
      addSubtask(task.id, newSubtaskTitle.trim());
      setNewSubtaskTitle('');
    }
  };

  const handleDeleteTask = () => {
    deleteTask(task.id);
  };

  const formatDueDate = (dateStr) => {
    if (!dateStr) return 'No date';
    const date = new Date(dateStr);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'MMM d, yyyy');
  };

  const getDueDateColor = (dateStr) => {
    if (!dateStr) return 'text-gray-500';
    const date = new Date(dateStr);
    if (isPast(date) && !isToday(date)) return 'text-red-500';
    if (isToday(date)) return 'text-blue-600';
    return 'text-gray-700';
  };

  const currentList = lists.find(l => l.id === task.listId);

  return (
    <aside className="w-full sm:w-96 h-screen bg-white border-l border-gray-200 flex flex-col shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <button
            onClick={handleClose}
            className="lg:hidden p-2 -ml-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-500" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900">Task Details</h2>
        </div>
        <Button variant="ghost" size="icon" onClick={handleClose} className="hidden lg:flex">
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Title */}
        <div className="p-4 border-b border-gray-100">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handleTitleBlur}
            className="text-lg font-medium border-none shadow-none px-0 focus-visible:ring-0"
            placeholder="Task title"
          />
        </div>

        {/* Due Date */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
              <PopoverTrigger asChild>
                <button
                  className={`flex-1 text-left text-sm ${getDueDateColor(task.dueDate)} hover:text-blue-600 transition-colors`}
                >
                  {formatDueDate(task.dueDate)}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={task.dueDate ? new Date(task.dueDate) : undefined}
                  onSelect={handleDateSelect}
                  initialFocus
                />
                {task.dueDate && (
                  <div className="p-2 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-red-500"
                      onClick={() => handleDateSelect(null)}
                    >
                      Clear date
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* List */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <ListTodo className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <Select value={task.listId} onValueChange={handleListChange}>
              <SelectTrigger className="flex-1 border-none shadow-none px-0 h-auto">
                <SelectValue placeholder="Select list">
                  {currentList?.name || 'Select list'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {lists.map(list => (
                  <SelectItem key={list.id} value={list.id}>
                    {list.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Tags */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-start gap-3">
            <Tag className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-700 mb-2">Tags</p>
              <div className="flex flex-wrap gap-2">
                {tags.map(tag => {
                  const isSelected = task.tagIds?.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      onClick={() => handleToggleTag(tag.id)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all
                        ${isSelected
                          ? 'ring-2 ring-offset-1'
                          : 'opacity-60 hover:opacity-100'
                        }`}
                      style={{
                        backgroundColor: `${tag.color}20`,
                        color: tag.color,
                        ringColor: isSelected ? tag.color : 'transparent',
                      }}
                    >
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                      {tag.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Subtasks */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-start gap-3">
            <ListTodo className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Subtasks
                {task.subtasks?.length > 0 && (
                  <span className="text-gray-400 font-normal ml-2">
                    ({task.subtasks.filter(s => s.completed).length}/{task.subtasks.length})
                  </span>
                )}
              </p>
              <div className="space-y-1">
                {task.subtasks?.map(subtask => (
                  <div
                    key={subtask.id}
                    className="group flex items-center gap-2 py-1.5 hover:bg-gray-50 rounded px-1 -mx-1 transition-colors"
                  >
                    <button
                      onClick={() => toggleSubtaskComplete(task.id, subtask.id)}
                      className={`w-4 h-4 rounded-full border flex-shrink-0 flex items-center justify-center transition-colors
                        ${subtask.completed
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-300 hover:border-blue-500'
                        }`}
                    >
                      {subtask.completed && <Check className="w-2.5 h-2.5 text-white" />}
                    </button>
                    <span className={`flex-1 text-sm truncate ${subtask.completed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                      {subtask.title}
                    </span>
                    <button
                      onClick={() => deleteSubtask(task.id, subtask.id)}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-all flex-shrink-0"
                    >
                      <X className="w-3 h-3 text-gray-400" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  placeholder="Add subtask..."
                  value={newSubtaskTitle}
                  onChange={(e) => setNewSubtaskTitle(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddSubtask()}
                  className="flex-1 h-8 text-sm"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleAddSubtask}
                  disabled={!newSubtaskTitle.trim()}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-700 mb-2">Notes</p>
              <Textarea
                placeholder="Add notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={handleNotesBlur}
                className="min-h-[100px] text-sm resize-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-100">
        <Button
          variant="outline"
          className="w-full text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
          onClick={handleDeleteTask}
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Delete Task
        </Button>
      </div>
    </aside>
  );
};

const TaskDetail = ({ onClose }) => {
  const {
    selectedTask,
    setSelectedTaskId,
    updateTask,
    deleteTask,
    lists,
    tags,
    addSubtask,
    deleteSubtask,
    toggleSubtaskComplete,
  } = useApp();

  if (!selectedTask) return null;

  // Use key to force re-mount when task changes, resetting form state
  return (
    <TaskDetailForm
      key={selectedTask.id}
      task={selectedTask}
      onClose={onClose}
      updateTask={updateTask}
      deleteTask={deleteTask}
      lists={lists}
      tags={tags}
      addSubtask={addSubtask}
      deleteSubtask={deleteSubtask}
      toggleSubtaskComplete={toggleSubtaskComplete}
      setSelectedTaskId={setSelectedTaskId}
    />
  );
};

export default TaskDetail;
